import { DocumentType } from "@api/graphql/generated/generated-types";
import { Label, Select } from "@src/common/components";
import { DOCUMENT_TYPE_LABELS } from "@src/driverDocuments/constants/documentTypeLabels";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "flowbite-react";

const DOCUMENT_TYPE_OPTIONS = Object.entries(DOCUMENT_TYPE_LABELS).map(
  ([value, label]) => ({ id: value, label }),
);

interface Props {
  infoText: string;
  showExcluded?: boolean;
  requiredDocTypes: DocumentType[];
  setRequiredDocTypes: (types: DocumentType[]) => void;
  warnedDocTypes: DocumentType[];
  setWarnedDocTypes: (types: DocumentType[]) => void;
  excludedDocTypes?: DocumentType[];
  setExcludedDocTypes?: (types: DocumentType[]) => void;
}

export function ComplianceSection({
  infoText,
  showExcluded = false,
  requiredDocTypes,
  setRequiredDocTypes,
  warnedDocTypes,
  setWarnedDocTypes,
  excludedDocTypes = [],
  setExcludedDocTypes,
}: Props) {
  const toSelectValue = (types: DocumentType[]) =>
    DOCUMENT_TYPE_OPTIONS.filter((o) => types.includes(o.id as DocumentType));

  const warnedSet = new Set<string>(warnedDocTypes);
  const requiredSet = new Set<string>(requiredDocTypes);
  const excludedSet = new Set<string>(excludedDocTypes);

  const requiredOptions = DOCUMENT_TYPE_OPTIONS.filter(
    (o) => !warnedSet.has(o.id) && !excludedSet.has(o.id),
  );
  const warnedOptions = DOCUMENT_TYPE_OPTIONS.filter(
    (o) => !requiredSet.has(o.id) && !excludedSet.has(o.id),
  );
  const excludedOptions = DOCUMENT_TYPE_OPTIONS.filter(
    (o) => !requiredSet.has(o.id) && !warnedSet.has(o.id),
  );

  return (
    <>
      <div className="mt-6 mb-2 border-t border-gray-200 dark:border-gray-700 pt-4">
        <div className="flex items-center gap-1 mb-4">
          <h3 className="text-base font-semibold">Driver Credentials</h3>
          <Tooltip content={infoText}>
            <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 cursor-pointer" />
          </Tooltip>
        </div>
      </div>
      <div className="mb-4">
        <div className="flex items-center gap-1 mb-2">
          <Label className="text-sm font-medium">
            Required Certifications
          </Label>
          <Tooltip content="Drivers missing these certifications will be blocked from being assigned to orders.">
            <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 cursor-pointer" />
          </Tooltip>
        </div>
        <Select
          multi
          size="compact"
          options={requiredOptions}
          placeholder="None"
          value={toSelectValue(requiredDocTypes)}
          onChange={(params) =>
            setRequiredDocTypes(
              params.value.map((v) => v.id as DocumentType),
            )
          }
          labelKey="label"
          valueKey="id"
        />
      </div>
      <div className="mb-4">
        <div className="flex items-center gap-1 mb-2">
          <Label className="text-sm font-medium">
            Recommended Certifications
          </Label>
          <Tooltip content="Dispatchers will see a warning when assigning drivers missing these certifications, but can proceed.">
            <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 cursor-pointer" />
          </Tooltip>
        </div>
        <Select
          multi
          size="compact"
          options={warnedOptions}
          placeholder="None"
          value={toSelectValue(warnedDocTypes)}
          onChange={(params) =>
            setWarnedDocTypes(
              params.value.map((v) => v.id as DocumentType),
            )
          }
          labelKey="label"
          valueKey="id"
        />
      </div>
      {showExcluded && setExcludedDocTypes && (
        <div className="mb-4">
          <div className="flex items-center gap-1 mb-2">
            <Label className="text-sm font-medium">
              Not Required Certifications
            </Label>
            <Tooltip content="These certifications will not be required even if set at a higher level.">
              <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 cursor-pointer" />
            </Tooltip>
          </div>
          <Select
            multi
            size="compact"
            options={excludedOptions}
            placeholder="None"
            value={toSelectValue(excludedDocTypes)}
            onChange={(params) =>
              setExcludedDocTypes(
                params.value.map((v) => v.id as DocumentType),
              )
            }
            labelKey="label"
            valueKey="id"
          />
        </div>
      )}
    </>
  );
}
